<script setup lang="ts">
import { computed } from 'vue';
import { PolicyEnforcement, type V1SnapshotOutreachRadius } from '@factoryfixinc/ats-interfaces';

import FormSection from '@/components/Jobs/CopilotActivation/FormSection.vue';
import ActiveOutreachOptions from '@/components/Jobs/CopilotActivation/ActiveOutreachOptions.vue';
import ScheduledUpdatesOptions from '@/components/Jobs/CopilotActivation/ScheduledUpdatesOptions.vue';
import SourcingPreferencesSection from '@/components/Jobs/CopilotActivation/ProjectCopilotConfiguration/SourcingPreferencesSection.vue';
import SourcingRadiusSelector from '@/components/Jobs/CopilotActivation/SourcingRadiusSelector.vue';
import SourcingScopeNote from '@/components/Jobs/CopilotActivation/SourcingScopeNote.vue';
import CopilotActivationService from '@/core/jobs/copilot-activation/copilot-activation.service';
import { isFieldContext } from '@/core/jobs/copilot-activation/location-context.utils';

const props = defineProps<{
  isDisabled: boolean;
  projectId?: number;
  copilotStatus: string;
  isContinueLogicEnabled: boolean;
  copilotOutreachPolicy?: PolicyEnforcement;
  scheduledUpdatesPolicy?: PolicyEnforcement;
}>();

const copilotActivationService = new CopilotActivationService();

const isDisabled = computed(() => props.isDisabled);
const projectId = computed(() => props.projectId);
const copilotStatus = computed(() => props.copilotStatus);

const isActiveOutreachEnabled = computed(
  () => copilotActivationService.activeOutreachEnabled === true,
);

const sourcingRadius = computed<V1SnapshotOutreachRadius>({
  get: () => copilotActivationService.projectCopilotConfigurationV1OutreachRadiusMiles,
  set: (value) => {
    copilotActivationService.projectCopilotConfigurationV1OutreachRadiusMiles = value;
  },
});

// Field contexts own the radius in the job details (ONE-7106): lives_near sets it next to the
// city it applies to, nationwide has no radius at all.
const isFieldLocation = computed(() => isFieldContext(copilotActivationService.locationContext));
</script>

<template>
  <FormSection
    id="sourcing-strategy"
    title="Align on your AI recruiter's sourcing strategy"
    subtitle="Choose where and how your AI recruiter finds the best candidates for this role"
    next-section-id="screening-style"
    add-divider
    :is-continue-logic-enabled="isContinueLogicEnabled"
  >
    <ActiveOutreachOptions
      :is-disabled="isDisabled"
      :copilot-outreach-policy="copilotOutreachPolicy"
    />

    <SourcingRadiusSelector
      v-if="isActiveOutreachEnabled && !isFieldLocation"
      v-model="sourcingRadius"
      :is-disabled="isDisabled"
      class="mt-3"
    />
    <SourcingScopeNote
      v-if="isActiveOutreachEnabled && isFieldLocation"
      data-test="sourcing-radius-note"
      class="mt-3"
      :location-context="copilotActivationService.locationContext"
      :city="copilotActivationService.city"
      :state="copilotActivationService.state"
      :radius-miles="sourcingRadius"
    />

    <ScheduledUpdatesOptions
      :is-disabled="isDisabled"
      :project-id="projectId"
      :copilot-status="copilotStatus"
      :scheduled-updates-policy="scheduledUpdatesPolicy"
    />
    <SourcingPreferencesSection :is-disabled="isDisabled" class="mt-6" />
  </FormSection>
</template>

<style lang="scss"></style>
